import {
  toWSDateString,
  WSAlert,
  WSAvatar,
  WSCard,
  WSElement,
  WSFlexBox,
  WSGrid,
  WSInfoBox,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import {
  getPathnameMyResourcesSubmitCancellation,
  getPathnameMyResourcesTab,
  PATH_MY_RESOURCES
} from "../paths";
import { DataViewSection } from "../../../shared/components/DataView";
import { WSQueries } from "../../../query/WSQuery";
import { selectorInsuranceCoverageStatusPillByExpirationDate } from "../selectors/selectorInsuranceCoverageStatusPillByExpirationDate";
import { useQueryPlanEnrollment } from "../../../query/benefits/useQueryPlanEnrollment";
import { selectorAboutPlanByPlanType } from "../selectors/selectorAboutPlanByPlanType";
import { routeHelpersBenefitDetail } from "../../Benefits/paths";
import { MyResourceInsuranceHistory } from "../components/MyResourceInsuranceHistory";
import { selectorPolicyNameByPolicyType } from "../selectors/selectorPolicyNameByPolicyType";
import { useModalStopCancellation } from "../components/useModalStopCancellation";
import { selectorInsuranceCoverageStatusDescriptionByInsurance } from "../selectors/selectorInsuranceCoverageStatusDescriptionByInsurance";
import { FilingAClaim } from "../../Benefits/components/FilingAClaim";
import { useGetNow } from "../../../shared/hooks/useGetNow";
import { InsuranceCoverage } from "@wingspanhq/wingspan-api-sdk";
import { useHistory } from "react-router-dom";
import { MyResourcesTab } from "../types";

type Props = {
  insuranceCoverage: InsuranceCoverage;
  tab?: MyResourcesTab;
};

export const PlanEnrollmentDetails: React.FC<Props> = ({
  insuranceCoverage,
  tab
}) => {
  const history = useHistory();
  const qPlanEnrollment = useQueryPlanEnrollment(
    insuranceCoverage.sourceId ?? "",
    { enabled: !!insuranceCoverage.sourceId }
  );

  const modalStopCancellation = useModalStopCancellation();

  const now = useGetNow();

  return (
    <WSPage colorBackground="gray50">
      <WSQueries queries={{ qPlanEnrollment }}>
        {({ qPlanEnrollmentData }) => {
          const isCancelled = !!qPlanEnrollmentData.endDate;
          const isArchived =
            qPlanEnrollmentData.endDate && qPlanEnrollmentData.endDate < now;

          const name = selectorPolicyNameByPolicyType(
            insuranceCoverage.policyType,
            qPlanEnrollmentData.planType
          );

          return (
            <>
              <WSPageToolbar
                title={name}
                actions={[
                  // {
                  //   label: "Download",
                  //   icon: "download",
                  //   buttonKind: "Primary",
                  //   onClick: () => {
                  //     alert("TODO!");
                  //   }
                  // },
                  {
                    onClick() {
                      modalStopCancellation.open({
                        planEnrollmentId: qPlanEnrollmentData.planEnrollmentId,
                        planType: qPlanEnrollmentData.planType
                      });
                    },
                    label: "Stop cancellation and keep enrollment",
                    icon: "redo",
                    visible: isCancelled && !isArchived
                  },
                  {
                    label: "Submit cancellation",
                    icon: "block",
                    destructive: true,
                    visible: !isCancelled && !isArchived,
                    onClick: () => {
                      history.push(
                        getPathnameMyResourcesSubmitCancellation(
                          qPlanEnrollmentData.planEnrollmentId
                        )
                      );
                    }
                  }
                ]}
                breadcrumbs={[
                  {
                    label: "My resources",
                    onClick: () => {
                      if (tab) {
                        return history.push(getPathnameMyResourcesTab(tab));
                      } else {
                        history.push(PATH_MY_RESOURCES);
                      }
                    }
                  },
                  {
                    label: name
                  }
                ]}
              />
              {isCancelled ? (
                <WSAlert
                  title={`${name} ending soon`}
                  theme="neutral"
                  icon="calendar"
                  action={{
                    onClick() {
                      modalStopCancellation.open({
                        planEnrollmentId: qPlanEnrollmentData.planEnrollmentId,
                        planType: qPlanEnrollmentData.planType
                      });
                    },
                    text: "Stop cancellation and keep enrollment"
                  }}
                  mb="XL"
                >
                  You’ve submitted a cancellation for this policy and it will
                  end on {toWSDateString(qPlanEnrollmentData.endDate)}. If you
                  change your mind, click “Stop cancellation and keep
                  enrollment” below.
                </WSAlert>
              ) : null}

              <WSGrid>
                <WSGrid.Item span={{ s: "12", m: "8" }}>
                  <WSList gap="XL">
                    <DataViewSection
                      gridColumns={3}
                      title="Policy information"
                      data={[
                        {
                          label: "Insurer",
                          value: insuranceCoverage.carrier
                        },
                        {
                          label: "Policy number",
                          value: insuranceCoverage.policyNumber
                        },
                        {
                          label: "Status",
                          pill: selectorInsuranceCoverageStatusPillByExpirationDate(
                            insuranceCoverage.expirationDate
                          ),
                          value: null,
                          hideValue: true,
                          secondaryText:
                            selectorInsuranceCoverageStatusDescriptionByInsurance(
                              insuranceCoverage,
                              now
                            )
                        }
                      ]}
                    >
                      <FilingAClaim planType={qPlanEnrollmentData.planType} />
                    </DataViewSection>
                    <WSPanel>
                      {selectorAboutPlanByPlanType(
                        qPlanEnrollmentData.planType
                      )}
                    </WSPanel>
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ s: "12", m: "4" }}>
                  <WSList gap="XL">
                    <WSElement>
                      <WSSectionToolbar title="Source" />
                      <WSCard
                        mt="S"
                        divider
                        actions={{
                          alignment: "fill",
                          buttons: [
                            {
                              kind: "Secondary",
                              label: "View insurance benefits",
                              onClick: () => {
                                history.push(
                                  routeHelpersBenefitDetail.createLocationObject(
                                    { slug: qPlanEnrollmentData.planType }
                                  )
                                );
                              }
                            }
                          ]
                        }}
                      >
                        <WSList gap="L">
                          <WSFlexBox.CenterY gap="M" wrap="nowrap">
                            <WSAvatar.Icon
                              icon="shield-check-fill"
                              colorBackground="garnet"
                            />
                            <WSText.Paragraph weight="medium">
                              {name}
                            </WSText.Paragraph>
                          </WSFlexBox.CenterY>

                          <WSInfoBox>
                            This policy was activated from an insurance benefit
                            you enrolled in on{" "}
                            {toWSDateString(insuranceCoverage.effectiveDate)}
                          </WSInfoBox>
                        </WSList>
                      </WSCard>
                    </WSElement>
                    <MyResourceInsuranceHistory
                      planType={qPlanEnrollmentData.planType}
                      isCompactView={true}
                      insuranceCoverageId={insuranceCoverage.id!}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
